import { useEffect, useState, useCallback } from "react";
import api, { inr } from "@/lib/api";
import OrdersTab from "@/components/OrdersTab";
import CustomersTab from "@/components/CustomersTab";
import ProductsTab from "@/components/ProductsTab";
import InstallGuideDialog from "@/components/InstallGuideDialog";
import { Button } from "@/components/ui/button";
import { Download, LogOut, ShoppingBag, Users, IndianRupee, TrendingUp, Monitor, ArrowLeft, User, Wallet, AlertCircle, Eye, EyeOff } from "lucide-react";
import ProfileDialog from "@/components/ProfileDialog";

const TABS = [
  { id: "orders", label: "Orders", hindi: "\u0911\u0930\u094D\u0921\u0930" },
  { id: "customers", label: "Customers", hindi: "\u0917\u094D\u0930\u093E\u0939\u0915" },
  { id: "products", label: "Products", hindi: "\u092A\u094D\u0930\u094B\u0921\u0915\u094D\u091F" },
];

export default function Dashboard({ shop, onLogout, onInstall, canInstall, onBack, onShopUpdate }) {
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showKpi, setShowKpi] = useState(() => localStorage.getItem("ps_hide_kpi") !== "1");
  const toggleKpi = () =>
    setShowKpi((v) => {
      localStorage.setItem("ps_hide_kpi", v ? "1" : "0");
      return !v;
    });

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [refreshKey]);

  const kpis = [
    { label: "Today Sale", sub: "Today", value: stats ? inr(stats.today_sales) : "…", icon: IndianRupee, color: "text-cyan-600 bg-cyan-50" },
    { label: "Total Orders", sub: "All Time", value: stats ? stats.total_orders : "…", icon: ShoppingBag, color: "text-pink-600 bg-pink-50" },
    { label: "Total Customer", sub: "All Time", value: stats ? stats.total_customers : "…", icon: Users, color: "text-yellow-600 bg-yellow-50" },
    { label: "Total Sale", sub: "All Time", value: stats ? inr(stats.grand_total) : "…", icon: TrendingUp, color: "text-slate-700 bg-slate-100" },
    { label: "Received", sub: "Payments", value: stats ? inr(stats.total_received) : "…", icon: Wallet, color: "text-emerald-600 bg-emerald-50" },
    { label: "Total Due", sub: "Pending", value: stats ? inr(stats.total_due) : "…", icon: AlertCircle, color: "text-red-500 bg-red-50" },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      <header className="sticky top-0 z-20 bg-white border-b border-border">
        <div className="h-1 flex">
          <div className="flex-1 bg-cyan-500" />
          <div className="flex-1 bg-pink-600" />
          <div className="flex-1 bg-yellow-400" />
          <div className="flex-1 bg-slate-900" />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2 sm:gap-3">
          {onBack && (
            <Button size="sm" variant="ghost" onClick={onBack} data-testid="back-to-features-btn" className="gap-1 text-muted-foreground hover:text-foreground -ml-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Features</span>
            </Button>
          )}
          <img src="/logo.webp?v=2" alt="Sootrik" className="h-8 sm:h-9 w-auto" data-testid="header-logo" />
          <div className="w-full sm:w-auto sm:flex-1 sm:min-w-0 order-2 sm:order-none">
            <p className="font-heading font-bold text-sm sm:text-base truncate" data-testid="header-shop-name">{shop.name}</p>
            <p className="text-xs text-muted-foreground truncate">Mob: {shop.mobile}</p>
          </div>
          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 ml-auto">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (canInstall) onInstall();
              else setShowInstallGuide(true);
            }}
            data-testid="pwa-install-btn"
            className="gap-1.5 border-cyan-500 text-cyan-700 hover:bg-cyan-50"
          >
            <Monitor className="h-4 w-4" />
            <span className="hidden sm:inline">Install App</span>
            <span className="sm:hidden">Install</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowProfile(true)} data-testid="profile-btn" className="gap-1.5 text-muted-foreground hover:text-foreground">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Profile</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={onLogout} data-testid="logout-btn" className="gap-1.5 text-muted-foreground hover:text-foreground">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex justify-end -mb-3">
          <button
            onClick={toggleKpi}
            data-testid="kpi-toggle-btn"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            title={showKpi ? "Summary chhupayein" : "Summary dikhayein"}
          >
            {showKpi ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showKpi ? "Hide Summary" : "Show Summary"}
          </button>
        </div>
        {showKpi ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="orders-summary-kpi">
          {kpis.map((k) => (
            <div key={k.label} className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{k.label}</p>
                <span className={`p-1.5 rounded-lg ${k.color}`}>
                  <k.icon className="h-4 w-4" />
                </span>
              </div>
              <p className="font-heading text-xl sm:text-2xl font-bold mt-2">{k.value}</p>
              <p className="text-xs text-muted-foreground">{k.sub}</p>
            </div>
          ))}
        </div>
        ) : (
          <div className="bg-card border border-dashed border-border rounded-xl p-4 text-center text-xs text-muted-foreground" data-testid="kpi-hidden-note">
            Click the Eye Icon to View Summary
          </div>
        )}

        <nav className="flex gap-2 border-b border-border" data-testid="dashboard-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                tab === t.id
                  ? "border-cyan-600 text-cyan-700 bg-cyan-50/60"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-slate-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {tab === "orders" && <OrdersTab onChanged={reload} />}
        {tab === "customers" && <CustomersTab onChanged={reload} />}
        {tab === "products" && <ProductsTab />}
      </main>

      <InstallGuideDialog
        open={showInstallGuide}
        onClose={() => setShowInstallGuide(false)}
        onDirectInstall={onInstall}
        canDirectInstall={canInstall}
      />
      <ProfileDialog
        open={showProfile}
        onClose={() => setShowProfile(false)}
        shop={shop}
        onUpdated={onShopUpdate}
      />
    </div>
  );
}

